import React from 'react';
import ReviewCard from '../feed/ReviewCard';

function ProfileView({
  user,
  isEditingProfile,
  editName,
  setEditName,
  editBio,
  setEditBio,
  editDescription,
  setEditDescription,
  editPrice,
  setEditPrice,
  editPicture,
  subscriptions,
  mySubscribers,
  monthlyRevenue,
  myReviews,
  savedReviewIds,
  loading,
  onEditProfile,
  onSaveProfile,
  onCancelEdit,
  onPictureUpload,
  onLogout,
  onManageSubscriptions,
  onCreateReview,
  onPhotoClick,
  onSaveReview,
  onUnsaveReview,
  onViewCreator,
  onUpgrade
}) {
  return (
    <div className="space-y-6" data-testid="profile-view">
      {!isEditingProfile ? (
        <>
          {/* Profile Info Card */}
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center gap-4 mb-6">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-xl font-heading font-bold">{user.name}</h2>
                {user.bio && <p className="text-sm text-gray-600 mt-1">{user.bio}</p>}
                <p className="text-xs text-gray-500 mt-1">{user.email}</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onEditProfile}
                className="flex-1 bg-primary text-white py-3 rounded-button font-semibold hover:shadow-hover"
              >
                Edit Profile
              </button>
              <button
                onClick={onLogout}
                className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-button font-semibold hover:bg-gray-50"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      ) : (
        /* Edit Profile Form */
        <div className="bg-white rounded-card shadow-card p-6">
          <h3 className="text-xl font-heading font-bold mb-4">Edit Profile</h3>
          
          {/* Profile Picture */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Profile Picture</label>
            <div className="flex items-center gap-4">
              {editPicture ? (
                <img src={editPicture} alt="Preview" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                  {editName?.charAt(0).toUpperCase()}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onPictureUpload}
                className="flex-1 text-sm"
              />
            </div>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-button px-4 py-2 focus:border-primary focus:outline-none"
              placeholder="Your name"
            />
          </div>

          {/* Headline (Bio) */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Headline</label>
            <input
              type="text"
              value={editBio}
              onChange={(e) => setEditBio(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-button px-4 py-2 focus:border-primary focus:outline-none"
              placeholder="e.g., SF food explorer sharing hidden gems 🌮"
              maxLength="100"
            />
            <p className="text-xs text-gray-500 mt-1">{editBio.length}/100 characters</p>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">About Me</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-button px-4 py-2 focus:border-primary focus:outline-none"
              placeholder="Tell people about your taste, what you look for in restaurants, your food philosophy..."
              rows="6"
            />
          </div>

          {/* Subscription Price */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Monthly Subscription Price</label>
            <div className="flex items-center gap-2">
              <span className="text-lg">$</span>
              <input
                type="number"
                value={editPrice}
                onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
                className="w-32 border-2 border-gray-200 rounded-button px-4 py-2 focus:border-primary focus:outline-none"
                min="0"
                step="1"
              />
              <span className="text-sm text-gray-600">per month</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Set to $0 to share reviews for free. Set a price to start monetizing your recommendations.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onCancelEdit}
              className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-button font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSaveProfile}
              disabled={loading}
              className="flex-1 bg-primary text-white py-3 rounded-button font-semibold hover:shadow-hover disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Subscriptions Card */}
      <div className="bg-white rounded-card shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Subscriptions</h3>
          <span className="text-2xl font-bold text-primary">{subscriptions.length}</span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          You're following {subscriptions.length} {subscriptions.length === 1 ? 'foodie' : 'foodies'}
        </p>
        <button
          onClick={onManageSubscriptions}
          className="w-full bg-primary text-white py-3 rounded-button font-semibold hover:shadow-hover"
          data-testid="manage-subscriptions-button"
        >
          Manage Subscriptions
        </button>
      </div>

      {/* Subscribers & Revenue Card (for creators) */}
      {user.role === 'creator' && (
        <>
          <div className="bg-white rounded-card shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Subscribers</h3>
              <span className="text-2xl font-bold text-secondary">{mySubscribers.length}</span>
            </div>
            <p className="text-sm text-gray-600">
              People following your recommendations
            </p>
          </div>

          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-card shadow-card p-6 border-2 border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-lg">Monthly Revenue</h3>
              <span className="text-3xl font-bold text-primary">${monthlyRevenue.toFixed(2)}</span>
            </div>
            <p className="text-sm text-gray-600">
              From {mySubscribers.filter(s => s.tier === 'premium').length} premium subscriber{mySubscribers.filter(s => s.tier === 'premium').length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* My Reviews Section */}
          <div className="space-y-4">
            <h3 className="text-xl font-heading font-bold">My Reviews ({myReviews.length})</h3>
            {myReviews.length === 0 ? (
              <div className="bg-white rounded-card shadow-card p-8 text-center">
                <p className="text-gray-500 mb-4">You haven't created any reviews yet</p>
                <button
                  onClick={onCreateReview}
                  className="bg-primary text-white px-6 py-3 rounded-button font-semibold hover:shadow-hover"
                >
                  Create Your First Review
                </button>
              </div>
            ) : (
              myReviews.map(review => (
                <ReviewCard
                  key={review.review_id}
                  review={{...review, subscription_tier: 'premium', creator: {user_id: user.user_id, name: user.name, picture: user.picture}}}
                  onPhotoClick={(index) => onPhotoClick({...review, subscription_tier: 'premium', creator: {user_id: user.user_id, name: user.name, picture: user.picture}}, index)}
                  onSave={() => onSaveReview(review.review_id, review.restaurant.name)}
                  onUnsave={() => onUnsaveReview(review.review_id, review.restaurant.name)}
                  isSaved={savedReviewIds.has(review.review_id)}
                  onViewCreator={onViewCreator}
                  onUpgrade={(creatorId, creatorName) => onUpgrade(creatorId, creatorName, 7.0)}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ProfileView;
